import capitalize from 'lodash/capitalize';
import downloadFile from 'downloadjs';
import isEmpty from 'lodash/isEmpty';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { fetchInfluencerResponseTime } from 'modules/influencers/reducers/influencerResponseTime';
import { receiveShortlistedSubcampaigns } from 'modules/subcampaigns/reducers/bulkOffer';
import { fetchCampaignOverview } from './campaignOverview';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post } from 'utils/api';
import {
  PARTICIPANT_TYPE_SHORTLISTED,
  PARTICIPANT_TYPE_TO_STATE_MAP
} from 'utils/campaignParticipants';
import { Querystring } from 'utils/url';

const RECEIVE = 'campaignParticipants/RECEIVE';
const RESET = 'campaignParticipants/RESET';
const SUBCAMPAIGN_REMOVED = 'campaignParticipants/SUBCAMPAIGN_REMOVED';
const LOADING_STATE_CHANGED = 'campaignParticipants/LOADING_STATE_CHANGED';

const { CAMPAIGNS, SUBCAMPAIGNS } = API.END_POINTS;

import type {
  Action,
  CampaignParticipantsQueryString,
  CampaignParticipantsRequestParams,
  Dispatch,
  Subcampaign,
  ThunkAction
} from 'types';
import type { AxiosRequestConfig } from 'axios';

type ParticipantStateOptions = {
  includeDeliverablesOverview?: boolean,
  includePayouts?: boolean
};

type State = {
  list: Array<Subcampaign>,
  isLoading: boolean
};

const initialState: State = {
  list: [],
  isLoading: true
};

function receiveParticipants(subcampaigns: Array<Subcampaign>): Action {
  return {
    type: RECEIVE,
    payload: {
      subcampaigns
    }
  };
}

export function loadingStateChanged(isLoading: boolean): Action {
  return {
    type: LOADING_STATE_CHANGED,
    payload: {
      isLoading
    }
  };
}

export function resetParticipants(): Action {
  return {
    type: RESET
  };
}

export function subcampaignRemovedFromParticipantList(subcampaign: Subcampaign): Action {
  return {
    type: SUBCAMPAIGN_REMOVED,
    payload: {
      subcampaign
    }
  };
}

export function resendExpiredSubcampaignOffers(campaignId: number): ThunkAction {
  return async (dispatch) => {
    const url = CAMPAIGNS.resendExpiredSubcampaignOffers(campaignId);

    dispatch(showLoading(url));

    try {
      await post(url, { data: {}, suppressGlobalNotification: true });

      return dispatch(fetchCampaignOverview(campaignId));
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

function getParticipantsQueryParamsByState(
  state: string,
  revokedBy?: string | null,
  options: ParticipantStateOptions = {}
): CampaignParticipantsRequestParams {
  const isShortlisted = state === PARTICIPANT_TYPE_SHORTLISTED;
  let query = '';

  if (!isShortlisted && options.includeDeliverablesOverview) {
    query += 'deliverables_overview';
  }

  if (options.includePayouts) {
    query += `${!isEmpty(query) ? ',' : ''}payouts`;
  }

  return {
    states: state || undefined,
    revokedByFilter: revokedBy || undefined,
    include: query as CampaignParticipantsQueryString
  };
}

export function downloadParticipants(
  campaignId: number,
  campaignName: string,
  type: string,
  revokedBy?: string | null,
  options: ParticipantStateOptions = {}
): ThunkAction {
  const urlWithoutQuery = CAMPAIGNS.downloadCampaignSubcampaigns(campaignId);
  const spec = PARTICIPANT_TYPE_TO_STATE_MAP[type];
  const requestConfig: AxiosRequestConfig = {
    headers: {
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    responseType: 'blob'
  };
  const fileName = `${campaignName} - ${capitalize(type)}.xlsx`;

  return (dispatch: Dispatch) => {
    if (!spec) {
      return Promise.resolve();
    }

    const queryString = Querystring.objectToString(
      getParticipantsQueryParamsByState(spec.state, revokedBy, options)
    );
    const url = `${urlWithoutQuery}${queryString ? `?${queryString}` : ''}`;

    dispatch(showLoading(url));

    return get(url, requestConfig)
      .then((response) => {
        downloadFile(response.data, fileName);
        dispatch(hideLoading(url));
      })
      .catch(() => dispatch(hideLoading(url)));
  };
}

export function getParticipantsByState(campaignId: number, params: CampaignParticipantsRequestParams): Promise<Array<Subcampaign>> {
  const url = CAMPAIGNS.getCampaignSubcampaigns(campaignId);
  const queryString = Querystring.objectToString(params);

  return get(`${url}${queryString ? `?${queryString}` : ''}`)
    .then(response => response.data)
    .catch(() => []);
}

function getParticipantsByType(
  campaignId: number,
  type: string,
  revokedBy?: string | null,
  options: ParticipantStateOptions = {}
): Promise<Array<Subcampaign>> {
  const spec = PARTICIPANT_TYPE_TO_STATE_MAP[type];

  if (!spec) {
    return Promise.resolve([]);
  }

  const params = getParticipantsQueryParamsByState(spec.state, revokedBy, options);

  return getParticipantsByState(campaignId, params);
}

export function fetchParticipants(
  campaignId: number,
  type: string,
  revokedBy?: string | null,
  options: ParticipantStateOptions = {}
): ThunkAction {
  return (dispatch) => {
    dispatch(loadingStateChanged(true));

    return getParticipantsByType(campaignId, type, revokedBy, options)
      .then(subcampaigns => {
        dispatch(receiveParticipants(subcampaigns));

        if (type === 'shortlisted') {
          dispatch(receiveShortlistedSubcampaigns(subcampaigns));
        }

        dispatch(loadingStateChanged(false));
        if ([PARTICIPANT_TYPE_SHORTLISTED].includes(type)) {
          dispatch(fetchInfluencerResponseTime(subcampaigns.map(s => s.creatorProfile.id)));
        }
      })
      .catch((error) => {
        dispatch(loadingStateChanged(false));

        throw error;
      });
  };
}

export function importCollection(campaignId: number, collectionIds: Array<number>): ThunkAction {
  return () => {
    const url = CAMPAIGNS.importCollection(campaignId);
    return post(url, {
      data: { creatorListIds: collectionIds }
    });
  };
}

export function clearShortlist(campaignId: number): ThunkAction {
  return async (dispatch) => {
    const url = CAMPAIGNS.clearShortlist(campaignId);

    dispatch(showLoading(url));

    await destroy(url);

    dispatch(hideLoading(url));
  };
}

export function removeSubcampaigns(ids: Array<number>): ThunkAction {
  return async (dispatch) => {
    const url = SUBCAMPAIGNS.removeSubcampaigns(ids);

    dispatch(showLoading(url));

    await destroy(url);

    dispatch(hideLoading(url));
  };
}

export default createReducer<State, any>('campaignParticipants', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RECEIVE]: (state, { subcampaigns }) => ({
    ...state,
    list: subcampaigns
  }),
  [SUBCAMPAIGN_REMOVED]: (state, { subcampaign }) => ({
    ...state,
    list: state.list.filter(s => s.id !== subcampaign.id)
  }),
  [LOADING_STATE_CHANGED]: (state, { isLoading }) => ({
    ...state,
    isLoading
  })
});
